/**
 * Read a JSON file out of the project's S3 bucket.
 *
 * The download endpoint does **not** return the file — it returns a presigned
 * URL (`{ url, fileName }`) that the browser then fetches directly from S3.
 * That second fetch goes through the download worker so a large report does not
 * block the UI thread, and through `/s3-proxy` in development because the
 * bucket does not allow the dev origin.
 *
 * This is the same sequence `useRunHistory` uses to load a single run's
 * summary. It lives here so the batch history does not have to copy it.
 *
 * @module shared/middleware/services/S3JsonService
 */

import { FILE_ENDPOINTS } from '../config/projectApiEndpoints';
import { noCacheAxios } from '../../config/cachedAxiosConfig';
import { getAuthHeaders } from '../../services/auth/authHeaders';
import { ProjectHeadersService } from './ProjectHeadersService';
import WorkerService from '../../workers/WorkerService';
import { createDownloadWorker } from '../../workers/downloadWorkerFactory';
import logger from '../../services/logging/logger';

const IS_DEV = process.env.NODE_ENV === 'development';

/** Auto-terminates after 60s idle. */
const downloadWorker = new WorkerService(createDownloadWorker);

/**
 * Strip the `projects/{projectId}/` prefix. The download endpoint takes a path
 * relative to the project, while the paths the run API hands back are absolute.
 */
export const toFileServicePath = (s3Path, projectId) => {
  if (!s3Path) return null;
  const prefix = `projects/${projectId}/`;
  return s3Path.startsWith(prefix) ? s3Path.slice(prefix.length) : s3Path;
};

/**
 * Presigned URL for an S3 object.
 *
 * @throws when the object does not exist — a 404 here means "not written yet"
 *         as often as it means "wrong path".
 */
export const getPresignedUrl = async (projectId, s3Path, userData) => {
  const baseHeaders = await getAuthHeaders();
  const headers = ProjectHeadersService.addProjectHeaders(baseHeaders, userData);
  const endpoint = FILE_ENDPOINTS.downloadFile(projectId, toFileServicePath(s3Path, projectId));
  const response = await noCacheAxios.get(endpoint, { headers });
  return response.data?.url ?? null;
};

/**
 * Fetch and parse a JSON object from S3.
 *
 * @returns the parsed JSON
 * @throws when the object is missing or unreadable
 */
export const fetchJson = async (projectId, s3Path, userData) => {
  const url = await getPresignedUrl(projectId, s3Path, userData);
  if (!url) throw new Error(`No presigned URL returned for ${s3Path}`);

  // In dev the proxy takes the real URL as a header, so the presigned URL's
  // own bucket host is preserved rather than being swapped for a fixed one.
  const payload = IS_DEV ? { url, proxyUrl: '/s3-proxy' } : { url };
  const json = await downloadWorker.run('FETCH_JSON', payload);
  logger.debug('S3 JSON loaded', { s3Path });
  return json;
};

const S3JsonService = { fetchJson, getPresignedUrl, toFileServicePath };

export default S3JsonService;
